import { useEffect } from 'react';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';

import { Logger } from '@core';

import { supabaseClient } from '../client';

const getUserQueryKey = () => ['user-session'];

export const useUser = () => {
    return useQuery({
        queryKey: getUserQueryKey(),
        queryFn: async () => {
            const {
                data: { user },
                error,
            } = await supabaseClient.auth.getUser();

            Logger.info(`USER LOAD: userId=${user?.id}`);
            if (error?.code) {
                Logger.error(`USER LOAD ERROR: ${error.name}, ${error.code}, ${error.message}`);
                throw error;
            }
            return user;
        },
        // useUserEvents update user state, because of that, we should disable all polling and just listen to the events.
        retry: false,
        retryOnMount: false,
        refetchOnMount: false,
        staleTime: Infinity,
    });
};

export const useConsumeQRCode = () => {
    return useMutation({
        mutationFn: async (qrCode: string) => {
            Logger.info(`USER CONSUME QR CODE ${qrCode}`);
            return await supabaseClient.functions.invoke('booth-qr-consume', {
                method: 'POST',
                body: { qrCode },
            });
        },
    });
};

export const useUserEvents = () => {
    const queryClient = useQueryClient();
    useEffect(() => {
        const {
            data: { subscription },
        } = supabaseClient.auth.onAuthStateChange((event, session) => {
            Logger.info(`USER EVENT: ${event}, ${session?.user?.id}`);

            if (event === 'SIGNED_OUT') {
                queryClient.setQueryData(getUserQueryKey(), null);
            }
            if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
                queryClient.setQueryData(getUserQueryKey(), session?.user ?? null);
            }
        });
        return () => {
            subscription.unsubscribe();
        };
    }, [queryClient]);
};

export const useUserLogin = () => {
    const navigate = useNavigate();
    return useMutation({
        mutationFn: async ({ email, password }: Record<'email' | 'password', string>) => {
            const { data, error } = await supabaseClient.auth.signInWithPassword({ email, password });
            Logger.info(`USER LOGIN: ${data.user?.id}`);
            if (error) {
                Logger.error(`USER LOGIN ERROR:${error.name}: ${error.message} (${error.code})`);
                throw error;
            }
            return data;
        },
        onSuccess: async () => {
            navigate('/', { replace: true });
        },
    });
};

export const useUserLogout = (loginPath: string) => {
    const queryClient = useQueryClient();
    const navigate = useNavigate();

    return useMutation({
        mutationFn: async () => {
            const { error } = await supabaseClient.auth.signOut();
            Logger.info(`USER LOGOUT`);
            if (error) {
                Logger.error(`USER LOGOUT ERROR: ${error.name}: ${error.message} (${error.code})`);
                throw error;
            }
        },
        onSuccess: () => {
            queryClient.clear();
            navigate(loginPath, { replace: true });
        },
    });
};

type RegisterInput = {
    email: string;
    password: string;
    name: string;
};

export const useUserRegister = (confirmationPath: string) => {
    const navigate = useNavigate();

    return useMutation({
        mutationFn: async ({ email, password, name }: RegisterInput) => {
            Logger.info('USER REGISTER: ' + email);
            const { data, error } = await supabaseClient.auth.signUp({
                email,
                password,
                options: {
                    data: { name },
                },
            });

            if (error) {
                Logger.error(`USER REGISTER ERROR: ${error.name} - ${error.message} (${error.code})`);
                throw error;
            }

            return data;
        },
        onSuccess: (data) => {
            const email = data.user?.email;

            if (!email) {
                throw new Error('Failed to read user email, please try again');
            }

            navigate(`${confirmationPath}?email=${email}`, { replace: true });
        },
    });
};

type ConfirmEmailInput = {
    email: string;
    token: string;
};

export const useUserConfirmEmail = () => {
    const navigate = useNavigate();

    return useMutation({
        mutationFn: async ({ email, token }: ConfirmEmailInput) => {
            Logger.info('USER CONFIRM EMAIL: ' + email);
            const { data, error } = await supabaseClient.auth.verifyOtp({
                email,
                token,
                type: 'signup',
            });

            if (error) {
                Logger.error(`USER CONFIRM EMAIL ERROR: ${error.name}: ${error.message} (${error.code})`);
                throw error;
            }

            return data;
        },
        onSuccess: () => {
            navigate('/', { replace: true });
        },
    });
};

export const useUserResetPassword = () => {
    return useMutation({
        mutationFn: async ({ email }: Record<'email', string>) => {
            Logger.info('USER RESET PASSWORD: ' + email);
            const { error } = await supabaseClient.auth.resetPasswordForEmail(email, {
                redirectTo: import.meta.env['VITE_PUBLIC_PASSWORD_RESET_URL'],
            });

            if (error) {
                Logger.error(
                    'USER RESET PASSWORD ERROR: ' + email + ` ${error.name}: ${error.message} (${error.code})`,
                );
                throw error;
            }

            return email;
        },
    });
};

export const useUserChangePassword = () => {
    const navigate = useNavigate();
    return useMutation({
        mutationFn: async (password: string) => {
            Logger.info('USER CHANGE PASSWORD');
            const { data, error } = await supabaseClient.auth.updateUser({
                password,
            });
            if (error) {
                Logger.error(`USER CHANGE PASSWORD ERROR: ${error.name}: ${error.message} (${error.code})`);
                throw error;
            }
            return data;
        },
        onSuccess: () => {
            return navigate('/', { replace: true });
        },
    });
};

export const useUserUpdate = () => {
    return useMutation({
        mutationFn: async ({ email, name }: { email: string; name: string }) => {
            Logger.info(`USER UPDATE: ${email}, ${name}`);
            const { data, error } = await supabaseClient.auth.updateUser({
                email: email,
                data: {
                    name,
                },
            });
            if (error) {
                Logger.error(`USER UPDATE ERROR: ${error.name}: ${error.message} (${error.code})`);
                throw error;
            }
            return data;
        },
    });
};

export const useResendConfirmationEmail = () => {
    return useMutation({
        mutationFn: async (email: string) => {
            Logger.info('USER RESEND CONFIRMATION: ' + email);

            const { error } = await supabaseClient.auth.resend({
                type: 'signup',
                email,
            });

            if (error) {
                Logger.error(`USER RESEND CONFIRMATION ERROR: ${error.name}: ${error.message} (${error.code})`);
                throw error;
            }

            return email;
        },
    });
};
